/**
 * Website-scrape (Dealer.com) configuration card for the Integrations page.
 * Edits the active dealer group's website_base_url + dealercom_site_id — the
 * site the Website Audit and the inventory ledger's live refresh scrape.
 * Both fields save together; clearing both disables the scrape for the group.
 */
import React, { useEffect, useState } from "react"

import {
  WebsiteScrapeConfig,
  fetchWebsiteScrapeConfig,
  updateWebsiteScrapeConfig,
} from "../../api/integrations"
import { THEME } from "../../config/theme"

interface Props {
  dealerGroupId: number
  canManage: boolean
}

// Card container matches components/integrations/VendorCard.tsx:88-91.
const cardStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.lg,
  padding: THEME.spacing.md,
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 13,
  fontFamily: THEME.fonts.primary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  boxSizing: "border-box",
}

// Caption-type label per THEME.typography.caption (12px / 500 / uppercase).
const labelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  display: "block",
  marginBottom: 4,
}

export default function WebsiteScrapeConfigCard({ dealerGroupId, canManage }: Props) {
  const [baseUrl, setBaseUrl] = useState("")
  const [siteId, setSiteId] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // Sync the form with the active group's stored config.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setSavedAt(null)
    ;(async () => {
      try {
        const config: WebsiteScrapeConfig = await fetchWebsiteScrapeConfig(dealerGroupId)
        if (!cancelled) {
          setBaseUrl(config.website_base_url ?? "")
          setSiteId(config.dealercom_site_id ?? "")
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load website config")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const saved = await updateWebsiteScrapeConfig(dealerGroupId, {
        website_base_url: baseUrl.trim() || null,
        dealercom_site_id: siteId.trim() || null,
      })
      setBaseUrl(saved.website_base_url ?? "")
      setSiteId(saved.dealercom_site_id ?? "")
      setSavedAt(Date.now())
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save website config")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
        Website scrape (Dealer.com)
      </div>
      <div
        style={{
          fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: THEME.spacing.md,
        }}
      >
        The site the Website Audit and the inventory ledger scrape for this dealer
        group. Leave both fields empty to disable the scrape.
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
          Loading…
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: THEME.spacing.md, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <label style={labelStyle}>Website base URL</label>
              <input
                style={inputStyle}
                value={baseUrl}
                placeholder="https://www.example-dealer.com"
                disabled={!canManage || saving}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label style={labelStyle}>Dealer.com site id</label>
              <input
                style={inputStyle}
                value={siteId}
                placeholder="exampledealer"
                disabled={!canManage || saving}
                onChange={(e) => setSiteId(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                marginTop: THEME.spacing.sm,
                fontSize: 13,
                color: THEME.colors.semantic.danger,
              }}
            >
              {error}
            </div>
          )}

          {canManage && (
            <div
              style={{
                marginTop: THEME.spacing.md,
                display: "flex",
                alignItems: "center",
                gap: THEME.spacing.sm,
              }}
            >
              {/* Brand primary button — matches buttonStyle in DownloadMenu.tsx. */}
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: THEME.fonts.primary,
                  color: THEME.colors.white,
                  background: THEME.colors.navy,
                  border: "none",
                  borderRadius: THEME.borderRadius.md,
                  cursor: saving ? "default" : "pointer",
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {savedAt !== null && !error && (
                <span style={{ fontSize: 13, color: THEME.colors.semantic.success }}>
                  Saved
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
